import React, { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { hasPermission } from '../utils/permissionUtils';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Container,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  useTheme,
  alpha
} from "@mui/material";
import LanguageIcon from '@mui/icons-material/Language';
import DescriptionIcon from '@mui/icons-material/Description';
import MapIcon from '@mui/icons-material/Map';
import SaveIcon from '@mui/icons-material/Save';

export default function WebsiteConfigurations() {
  const theme = useTheme();
  
  const [websiteAddress, setWebsiteAddress] = useState("");
  const [websiteDescription, setWebsiteDescription] = useState("");
  const [websiteSitemap, setWebsiteSitemap] = useState("");

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { chatbotId: paramsChatbotId } = useParams();

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('configuration', 'view');
        const modifyPerm = await hasPermission('configuration', 'add|edit');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanModify(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  // Fetch initial data on component mount
  useEffect(() => {
    if (canView) {
      fetchData();
    }
  }, [canView]);

  const fetchData = () => {
    setIsLoading(true);
    setMessage("");
    const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
    
    axiosInstance
      .get(`/api/website-configuration`, { params })
      .then((response) => {
        const responseData = typeof response.data.data === 'string' 
          ? JSON.parse(response.data.data) 
          : response.data.data;

        if (responseData && responseData.length > 0) {
          const latestConfig = responseData[responseData.length - 1];
          setWebsiteAddress(latestConfig.website_address || "");
          setWebsiteDescription(latestConfig.website_description || "");
          setWebsiteSitemap(latestConfig.website_sitemap || "");
        } else if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          setWebsiteAddress(responseData.website_address || "");
          setWebsiteDescription(responseData.website_description || "");
          setWebsiteSitemap(responseData.website_sitemap || "");
        } else {
          console.log("No configuration data found or unexpected format.");
          setWebsiteAddress("");
          setWebsiteDescription("");
          setWebsiteSitemap("");
        }
      })
      .catch((error) => {
        const errorMsg = error.response?.data?.message || "Error fetching website configurations.";
        console.error(errorMsg);
        setMessage(errorMsg);
        setMessageType("error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSaveButtonClick = () => {
    setIsSaving(true);
    setMessage("");

    const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
    const data = {
      website_address: websiteAddress,
      website_description: websiteDescription,
      website_sitemap: websiteSitemap,
    };

    axiosInstance
      .post(`/api/website-configuration`, data, { params })
      .then((response) => {
        setMessage(response.data.message || "Configuration saved successfully!");
        setMessageType("success");
        console.log("Success:", response.data.message);
      })
      .catch((error) => {
        console.error("Error saving data:", error);
        let errorMessage = "Error saving configuration.";
        
        if (error.response) {
          errorMessage = error.response.data.message || `Server Error: ${error.response.status}`;
        } else if (error.request) {
          errorMessage = "No response from server. Check network connection or backend status.";
        } else {
          errorMessage = error.message;
        }
        
        setMessage(errorMessage);
        setMessageType("error");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '400px' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body1" color="textSecondary">Checking permissions...</Typography>
        </Stack>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Access Denied</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You don't have permission to view Website settings. Please contact your administrator.
          </Typography>
        </Alert>
      </Container>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px' }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }} color="textPrimary">
          Website Configurations
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '300px' }}>
          <Stack alignItems="center" spacing={2}>
            <CircularProgress />
            <Typography variant="body1" color="textSecondary">Loading configurations...</Typography>
          </Stack>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
      <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 700 }} color="textPrimary">
        Website Configurations
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {/* Display Message */}
      {message && (
        <Alert 
          severity={messageType} 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => setMessage("")}
        >
          {message}
        </Alert>
      )}

      <Card 
        variant="outlined" 
        sx={{ 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Website Address */}
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LanguageIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600} color="textPrimary">
                    Website Address
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  placeholder="https://example.com"
                  value={websiteAddress}
                  onChange={(e) => setWebsiteAddress(e.target.value)}
                  disabled={isSaving}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Grid>

            {/* Website Description */}
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DescriptionIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600} color="textPrimary">
                    Title / Description
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  placeholder="Enter website title or description"
                  value={websiteDescription}
                  onChange={(e) => setWebsiteDescription(e.target.value)}
                  disabled={isSaving}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Grid>

            {/* Website Sitemap */}
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MapIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={600} color="textPrimary">
                    Website Sitemap URL
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  placeholder="https://example.com/sitemap.xml"
                  value={websiteSitemap}
                  onChange={(e) => setWebsiteSitemap(e.target.value)}
                  disabled={isSaving}
                  size="small"
                  variant="outlined"
                />
              </Stack>
            </Grid>
          </Grid>

          {/* Save Button */}
          {canModify && (
            <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveButtonClick}
                disabled={isSaving}
                size="large"
                sx={{ minWidth: 200 }}
              >
                {isSaving ? "Saving..." : "Save Configuration"}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Info Section */}
      <Alert 
        severity="info" 
        sx={{ 
          mt: 3, 
          borderRadius: 2,
          bgcolor: alpha(theme.palette.info.main, 0.08)
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          Configuration Guidelines
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Website Address:</strong> Enter your main website URL (e.g., https://example.com)
          <br />
          • <strong>Title/Description:</strong> Brief description or title for your website
          <br />
          • <strong>Sitemap URL:</strong> Full URL to your website's sitemap.xml file
        </Typography>
      </Alert>
    </Paper>
  );
}